import { useState, useEffect } from "react";
import { PublicClientApplication, Configuration, AccountInfo } from "@azure/msal-browser";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

interface MicrosoftSsoButtonProps {
  onLoginSuccess: () => void;
  disabled?: boolean;
}

export default function MicrosoftSsoButton({ onLoginSuccess, disabled }: MicrosoftSsoButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);

  const { entraConfig, exchangeEntraToken, isSsoEnabled, isLoadingEntraConfig } = useAuth();

  // Initialize MSAL
  useEffect(() => {
    const initializeMsal = async () => {
      if (!entraConfig || !isSsoEnabled) return;

      try {
        const msalConfig: Configuration = {
          auth: {
            clientId: entraConfig.clientId || '',
            authority: entraConfig.authority,
            redirectUri: window.location.origin,
          },
          cache: {
            cacheLocation: "localStorage",
            storeAuthStateInCookie: false,
          }
        };

        const instance = new PublicClientApplication(msalConfig);
        await instance.initialize();

        // Handle redirect response (if user refreshes page during auth)
        await instance.handleRedirectPromise();

        setMsalInstance(instance);

        // Store config for token refresh later
        localStorage.setItem('entra_config', JSON.stringify(entraConfig));
      } catch (err) {
        console.error('Failed to initialize MSAL:', err);
        setError('Failed to initialize Microsoft authentication');
      }
    };

    initializeMsal();
  }, [entraConfig, isSsoEnabled]);

  const handleMicrosoftLogin = async () => {
    if (!msalInstance || !entraConfig) {
      setError('Microsoft SSO is not configured');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      // Use popup flow for better UX
      const loginRequest = {
        scopes: entraConfig.scopes || ["User.Read", "email", "profile", "offline_access"]
      };

      const response = await msalInstance.loginPopup(loginRequest);

      // Store MSAL account for token refresh
      localStorage.setItem('msal_account', JSON.stringify(response.account));
      localStorage.setItem('msal_initialized', 'true');
      localStorage.setItem('auth_method', 'Entra');

      // Exchange Microsoft token for Athlyn token
      const result = await exchangeEntraToken.mutateAsync({
        entraAccessToken: response.accessToken,
        microsoftTokens: {
          accessToken: response.accessToken,
          refreshToken: '', // Not available in implicit flow
          expiresAt: response.expiresOn?.toISOString() || '',
          scope: loginRequest.scopes.join(' ')
        }
      });

      // Check if user has CMS access
      const hasRoleAccess = result.user.roles?.some(role => ['AthlynAdmin', 'AthlynTeam'].includes(role));
      const hasAccessTypePermission = result.user.accessTypes?.some(accessType =>
        ['CmsPortal'].includes(accessType)
      );

      if (!hasRoleAccess && !hasAccessTypePermission) {
        throw new Error('Access denied. You do not have permission to access the CMS portal.');
      }

      onLoginSuccess();

    } catch (err: any) {
      console.error('Microsoft login failed:', err);

      // Handle specific MSAL errors
      if (err.errorCode === 'popup_window_error') {
        setError('Please enable popups for this site and try again');
      } else if (err.errorCode === 'user_cancelled') {
        setError('Login cancelled');
      } else {
        setError(err.message || 'Failed to sign in with Microsoft');
      }

      setIsLoading(false);
    }
  };

  if (isLoadingEntraConfig) {
    return (
      <Button disabled className="w-full">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading SSO Configuration...
      </Button>
    );
  }

  if (!isSsoEnabled) {
    return null;
  }

  return (
    <div className="space-y-2">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={handleMicrosoftLogin}
        disabled={disabled || isLoading || !msalInstance}
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Signing in...
          </>
        ) : (
          <>
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zM24 11.4H12.6V0H24v11.4z"
              />
            </svg>
            Continue with Microsoft
          </>
        )}
      </Button>
    </div>
  );
}
